import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../contexts/AuthContext';
import { ChangePasswordModal } from '../auth/ChangePasswordModal';

interface UserAvatarProps {
  onNavigate?: (section: string) => void;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  // Calculate dropdown position when opened - use useLayoutEffect to calculate before paint
  useLayoutEffect(() => {
    if (isDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Use getBoundingClientRect for fixed positioning (viewport coordinates)
      // Align right edge of menu with right edge of button
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      // Use a slight delay to avoid capturing the click that opened it
      requestAnimationFrame(() => {
    document.addEventListener('mousedown', handleClickOutside);
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleToggleDropdown = () => {
    if (!isDropdownOpen && buttonRef.current) {
      // Calculate position BEFORE opening to prevent flash
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right // Align right edge of menu with right edge of button
      });
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  if (!user) return null;

  // Generate initials from first and last name, fallback to username
  const getInitials = () => {
    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    if (user.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    return user.username.substring(0, 2).toUpperCase();
  };

  const handleMenuItemClick = (action: string) => {
    setIsDropdownOpen(false);
    
    switch (action) {
      case 'dashboard':
        onNavigate?.('dashboard');
        break;
      case 'admin':
        onNavigate?.('admin');
        break;
      case 'moderation':
        onNavigate?.('moderation');
        break;
      case 'change-password':
        setIsChangePasswordOpen(true);
        break;
      case 'logout':
        logout(() => navigate({ to: '/' }));
        break;
    }
  };

  const menuItems = [
    // User Dashboard (for everyone)
    {
      id: 'dashboard',
      label: 'User Dashboard',
      action: 'dashboard',
    },
    
    // Admin Dashboard (only for administrators)
    ...(user.role === 'ADMINISTRATOR' ? [
      {
        id: 'admin',
        label: 'Admin Dashboard',
        action: 'admin',
      }
    ] : []),
    
    // Moderation Dashboard (for moderators and administrators)
    ...(user.role === 'MODERATOR' || user.role === 'ADMINISTRATOR' ? [
      {
        id: 'moderation',
        label: 'Moderator Dashboard',
        action: 'moderation',
      }
    ] : []),

    // Change password (local accounts only)
    ...(user.authProvider === 'LOCAL' ? [
      {
        id: 'change-password',
        label: 'Change Password',
        action: 'change-password',
      }
    ] : []),
    
    // Sign Out (for everyone)
    {
      id: 'logout',
      label: 'Sign Out',
      action: 'logout',
    }
  ];

  const dropdownContent = isDropdownOpen ? (
        <div 
          ref={dropdownRef}
      className="fixed w-64 rounded-lg shadow-xl border z-[9999]"
          style={{ 
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)',
        boxShadow: 'var(--shadow-lg)',
        transform: 'translateX(0)' // Ensure no transform offset
          }}
        >
          {/* User Info Header */}
          <div className="p-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
            <div className="flex items-center space-x-3">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold"
                style={{ 
                  backgroundColor: 'var(--color-accent)',
                  color: 'white'
                }}
              >
                {getInitials()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                  {user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName}`
                    : user.username
                  }
                </p>
                <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                  {user.email}
                </p>
                <div className="mt-1">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" style={{ 
                    backgroundColor: 'var(--color-bg-tertiary)',
                    color: 'var(--color-text-secondary)'
                  }}>
                    {user.role === 'ADMINISTRATOR' ? '👑 Admin' :
                     user.role === 'MODERATOR' ? '🛡️ Moderator' :
                     '👤 User'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleMenuItemClick(item.action)}
                className="w-full text-left px-4 py-2 text-sm transition-colors"
                style={{ color: 'var(--color-text-secondary)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                  e.currentTarget.style.color = 'var(--color-text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-secondary)';
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
  ) : null;

  return (
    <>
      {/* Avatar Button */}
      <button 
        ref={buttonRef}
        onClick={handleToggleDropdown}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200"
        style={{ 
          backgroundColor: isDropdownOpen ? 'var(--color-bg-tertiary)' : 'var(--color-bg-secondary)',
          color: 'var(--color-text-primary)',
          border: '1px solid var(--color-border)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
          e.currentTarget.style.boxShadow = '0 2px 4px 0 rgba(0, 0, 0, 0.1)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isDropdownOpen ? 'var(--color-bg-tertiary)' : 'var(--color-bg-secondary)';
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <div 
          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-transform"
          style={{ 
            backgroundColor: 'var(--color-accent)',
            color: 'white',
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.15)'
          }}
        >
          {getInitials()}
        </div>
        <span className="text-xs transition-transform" style={{ 
          transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          color: 'var(--color-text-secondary)'
        }}>
          ▼
        </span>
      </button>

      {/* Dropdown Menu - Rendered via Portal */}
      {dropdownContent && createPortal(dropdownContent, document.body)}

      <ChangePasswordModal
        open={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
    </>
  );
};
